// Copyright (C) 2017-2023 Smart code 203358507

const React = require('react');
const PropTypes = require('prop-types');
const classnames = require('classnames');
const { useTranslation } = require('react-i18next');
const { default: Icon } = require('@stremio/stremio-icons/react');
const { Button, Image, Multiselect } = require('stremio/components');
const { useServices } = require('stremio/services');
const Stream = require('./Stream');
const styles = require('./styles');
const { usePlatform } = require('stremio/common');

const ALL_ADDONS_KEY = 'ALL';
const ALL_RESOLUTIONS_KEY = 'ALL';

const StreamsList = ({ className, video, ...props }) => {
    const { t } = useTranslation();
    const { core } = useServices();
    const platform = usePlatform();
    const streamsContainerRef = React.useRef(null);
    const [selectedAddon, setSelectedAddon] = React.useState(ALL_ADDONS_KEY);
    const [selectedResolution, setSelectedResolution] = React.useState(ALL_RESOLUTIONS_KEY);
    const onAddonSelected = React.useCallback((event) => {
        if (streamsContainerRef && streamsContainerRef.current) {
            streamsContainerRef.current.scrollTo({ top: 0, left: 0, behavior: platform.name === 'ios' ? 'smooth' : 'instant' });
        }
        setSelectedAddon(event.value);
    }, [platform]);
    const onResolutionSelected = React.useCallback((event) => {
        if (streamsContainerRef && streamsContainerRef.current) {
            streamsContainerRef.current.scrollTo({ top: 0, left: 0, behavior: platform.name === 'ios' ? 'smooth' : 'instant' });
        }
        setSelectedResolution(event.value);
    }, [platform]);
    const backButtonOnClick = React.useCallback(() => {
        if (video.deepLinks && typeof video.deepLinks.metaDetailsVideos === 'string') {
            window.location.replace(video.deepLinks.metaDetailsVideos + (
                typeof video.season === 'number' ?
                    `?${new URLSearchParams({'season': video.season})}`
                    :
                    null
            ));
        } else {
            window.history.back();
        }
    }, [video]);
    const countLoadingAddons = React.useMemo(() => {
        return props.streams.filter((stream) => stream.content.type === 'Loading').length;
    }, [props.streams]);
    const streamsByAddon = React.useMemo(() => {
        return props.streams
            .filter((streams) => streams.content.type === 'Ready')
            .reduce((streamsByAddon, streams) => {
                streamsByAddon[streams.addon.transportUrl] = {
                    addon: streams.addon,
                    streams: streams.content.content.map((stream) => ({
                        ...stream,
                        onClick: () => {
                            core.transport.analytics({
                                event: 'StreamClicked',
                                args: {
                                    stream
                                }
                            });
                        },
                        addonName: streams.addon.manifest.name
                    }))
                };

                return streamsByAddon;
            }, {});
    }, [props.streams]);
    const filteredStreams = React.useMemo(() => {
        let result = [];
        if (selectedAddon === ALL_ADDONS_KEY) {
            result = Object.values(streamsByAddon).map(({ streams }) => streams).flat(1);
        } else {
            if (streamsByAddon[selectedAddon]) {
                result = streamsByAddon[selectedAddon].streams;
            }
        }

        if (selectedResolution !== ALL_RESOLUTIONS_KEY) {
            result = result.filter((stream) => stream.description.includes(selectedResolution));
        }

        return result;
    }, [streamsByAddon, selectedAddon, selectedResolution]);
    const selectableOptions = React.useMemo(() => {
        const replaceName = (name) => {
            if (name.toLowerCase().includes('comet')) {
                return "V1"
            } else if (name.toLowerCase().includes('stremthru')) {
                return "V2"
            } else {
                return "Unknown"
            }
        }
        // const nameReplacement = {
        //     'Comet | PM': 'V1',
        //     'StremThru(ST|Torrentio RD)': 'V2',
        // };
        return {
            title: 'Select Module',
            options: [
                {
                    value: ALL_ADDONS_KEY,
                    label: t('ALL_ADDONS'),
                    title: t('ALL_ADDONS')
                },
                ...Object.keys(streamsByAddon).map((transportUrl) => ({
                    value: transportUrl,
                    label: replaceName(streamsByAddon[transportUrl].addon.manifest.name),
                    title: replaceName(streamsByAddon[transportUrl].addon.manifest.name),
                }))
            ],
            selected: [selectedAddon],
            onSelect: onAddonSelected
        };
    }, [streamsByAddon, selectedAddon]);
    const selectableResolutions = React.useMemo(() => {
        return {
            title: 'Select Resolution',
            options: [
                {
                    value: ALL_RESOLUTIONS_KEY,
                    label: 'Any Resolutions',
                    title: 'Any Resolutions',
                },
                {
                    value: '2160p',
                    label: '2160p',
                    title: '2160p',
                },
                {
                    value: '1080p',
                    label: '1080p',
                    title: '1080p',
                },
                {
                    value: '720p',
                    label: '720p',
                    title: '720p',
                },
                {
                    value: '480p',
                    label: '480p',
                    title: '480p',
                },
            ],
            selected: [selectedResolution],
            onSelect: onResolutionSelected
        };
    }, [selectedResolution]);
    return (
        <div className={classnames(className, styles['streams-list-container'])}>
            <div className={styles['select-choices-wrapper']}>
                <div className={styles['select-choices-wrapper-child']}>
                    {
                        video ?
                            <React.Fragment>
                                <Button className={classnames(styles['button-container'], styles['back-button-container'])} tabIndex={-1} onClick={backButtonOnClick}>
                                    <Icon className={styles['icon']} name={'chevron-back'} />
                                </Button>
                                <div className={styles['episode-title']}>
                                    {`S${video?.season}E${video?.episode} ${(video?.title)}`}
                                </div>
                            </React.Fragment>
                            :
                            null
                    }
                </div>
                <div className={styles['select-choices-wrapper-child']}>
                    {
                        Object.keys(streamsByAddon).length > 1 ?
                            <Multiselect
                                {...selectableOptions}
                                className={styles['select-input-container']}
                            />
                            :
                            null
                    }
                    <Multiselect
                        {...selectableResolutions}
                        className={styles['select-input-container']}
                    />
                </div>
            </div>
            {
                props.streams.length === 0 ?
                    <div className={styles['message-container']}>
                        <Image className={styles['image']} src={require('/images/empty.png')} alt={' '} />
                        <div className={styles['label']}>No modules were requested for streams!</div>
                    </div>
                    :
                    props.streams.every((streams) => streams.content.type === 'Err') ?
                        <div className={styles['message-container']}>
                            <Image className={styles['image']} src={require('/images/empty.png')} alt={' '} />
                            <div className={styles['label']}>{t('NO_STREAM')}</div>
                        </div>
                        :
                        filteredStreams.length === 0 && selectedResolution === ALL_RESOLUTIONS_KEY ?
                            <div className={styles['streams-container']}>
                                <Stream.Placeholder />
                                <Stream.Placeholder />
                            </div>
                            :
                            <React.Fragment>
                                {
                                    countLoadingAddons > 0 ?
                                        <div className={styles['addons-loading-container']}>
                                            <div className={styles['addons-loading']}>
                                                {countLoadingAddons} modules are still loading
                                            </div>
                                            <span className={styles['addons-loading-bar']}></span>
                                        </div>
                                        :
                                        null
                                }
                                <div className={styles['streams-container']} ref={streamsContainerRef}>
                                    {filteredStreams.map((stream, index) => (
                                        <Stream
                                            key={index}
                                            videoId={video?.id}
                                            videoReleased={video?.released}
                                            addonName={stream.addonName}
                                            name={stream.name}
                                            description={stream.description}
                                            thumbnail={stream.thumbnail}
                                            progress={stream.progress}
                                            deepLinks={stream.deepLinks}
                                            onClick={stream.onClick}
                                        />
                                    ))}
                                </div>
                            </React.Fragment>
            }
        </div>
    );
};

StreamsList.propTypes = {
    className: PropTypes.string,
    streams: PropTypes.arrayOf(PropTypes.object).isRequired,
    video: PropTypes.object
};

module.exports = StreamsList;
